import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Bell, X, MessageCircle, Sparkles } from "lucide-react";
import { toast } from "sonner";
import usePushNotifications from "@/hooks/usePushNotifications";

/**
 * Auto-prompt for push notifications on first visit
 * Shows a friendly modal encouraging students to enable notifications
 */
const NotificationPrompt = ({ studentId, studentName, onDismiss }) => {
  const { isSupported, isSubscribed, permission, subscribe, loading } = usePushNotifications(studentId);
  const [visible, setVisible] = useState(true);

  // Don't show if not supported or already subscribed
  if (!isSupported || isSubscribed || permission === 'denied') {
    return null;
  }

  const handleEnable = async () => {
    const success = await subscribe();
    if (success) {
      toast.success("Parfait ! Les philosophes pourront maintenant vous contacter.", {
        description: "Socrate, Descartes et les autres vous enverront des messages pour vous motiver !",
        duration: 5000
      });
      setVisible(false);
      onDismiss?.();
    } else {
      toast.error("Les notifications n'ont pas pu être activées", {
        description: "Vous pouvez réessayer plus tard dans les paramètres."
      });
    }
  };

  const handleDismiss = () => {
    localStorage.setItem("sophia_notification_prompt_dismissed", "true");
    setVisible(false);
    onDismiss?.();
  };

  if (!visible) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" data-testid="notification-prompt">
      <Card className="max-w-md w-full bg-white shadow-2xl animate-in fade-in zoom-in duration-300">
        <CardContent className="p-6">
          {/* Close button */}
          <button
            onClick={handleDismiss}
            className="absolute top-4 right-4 text-gray-400 hover:text-gray-600"
          >
            <X className="w-5 h-5" />
          </button>

          {/* Icon */}
          <div className="flex justify-center mb-4">
            <div className="w-16 h-16 rounded-full bg-gradient-to-br from-[#D4AF37] to-[#B3932E] flex items-center justify-center">
              <Bell className="w-8 h-8 text-white" />
            </div>
          </div>

          {/* Title */}
          <h2 className="text-xl font-bold text-[#1A365D] text-center mb-2" style={{ fontFamily: 'Playfair Display, serif' }}>
            Les philosophes veulent vous parler !
          </h2>

          {/* Description */}
          <p className="text-[#64748B] text-center mb-4">
            Activez les notifications pour recevoir des messages philosophiques personnalisés qui vous aideront à préparer le Bac.
          </p>

          {/* Examples */}
          <div className="bg-[#F8F9FA] rounded-lg p-4 mb-6 space-y-3">
            <div className="flex items-start gap-3">
              <MessageCircle className="w-5 h-5 text-[#D4AF37] mt-0.5 flex-shrink-0" />
              <div>
                <p className="text-sm font-medium text-[#1A365D]">Socrate</p>
                <p className="text-xs text-[#64748B] italic">"Connais-toi toi-même... reviens philosopher avec moi !"</p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <MessageCircle className="w-5 h-5 text-[#D4AF37] mt-0.5 flex-shrink-0" />
              <div>
                <p className="text-sm font-medium text-[#1A365D]">Descartes</p>
                <p className="text-xs text-[#64748B] italic">"Je pense, donc je suis... mais penses-tu assez ?"</p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <Sparkles className="w-5 h-5 text-[#D4AF37] mt-0.5 flex-shrink-0" />
              <div>
                <p className="text-sm font-medium text-[#1A365D]">Défi du jour</p>
                <p className="text-xs text-[#64748B] italic">"Qu'est-ce que la liberté ?" - Une question pour réfléchir</p>
              </div>
            </div>
          </div>

          {/* Buttons */}
          <div className="flex gap-3">
            <Button
              variant="outline"
              onClick={handleDismiss}
              className="flex-1 border-[#E2E8F0] text-[#64748B]"
            >
              Plus tard
            </Button>
            <Button
              onClick={handleEnable}
              disabled={loading}
              className="flex-1 bg-[#D4AF37] hover:bg-[#B3932E] text-[#0F172A]"
            >
              {loading ? "Activation..." : "Activer"}
            </Button>
          </div>

          {/* Note */}
          <p className="text-xs text-[#94A3B8] text-center mt-4">
            📱 Notifications envoyées après les cours (17h-18h) ou le matin le week-end
          </p>
        </CardContent>
      </Card>
    </div>
  );
};

export default NotificationPrompt;
